import { CurrencyPipe } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChange,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { NgForm } from '@angular/forms';
import {
  AccountViewModel,
  ACCOUNT_TEMPLATE_TYPES,
} from '@models/account.class';
import { IContract } from '@models/contract.class';
import { EventError } from '@models/tracking-error-event.class';
import { EventSuccess } from '@models/tracking-success-event.class';
import { Store } from '@ngrx/store';
import { capitalize, isEqual, isFinite } from 'lodash';
import moment from 'moment-timezone';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { filter, map, pluck, tap } from 'rxjs/operators';
import { AppState } from 'src/app';
import { TRACKING_EVENTS } from 'src/app/app.constants';
import {
  ContractsService,
  NotificationService,
  OperationService,
} from 'src/core';
import { CashLayers } from 'src/core/models/withdrawal.class';
import { MixpanelService } from 'src/core/services/mixpanel.service';
import { TradeTransferModel, TradeTransferSources } from './tradeTranfer.class';

@Component({
  selector: 'hb-trade-transfer',
  templateUrl: './tradeTransfer.component.html',
  styleUrls: ['./tradeTransfer.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class TradeTransferComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('tradeTransferForm') form: NgForm;

  @Input() currentContract: IContract;
  @Input() transferAccounts: AccountViewModel[];
  @Input() isGeolocationEnabled: boolean;
  @Input() isUsaContract: boolean;
  @Input() getContractCashLayers$: Observable<CashLayers> = new Observable();

  @Output() onTranfered: EventEmitter<boolean> = new EventEmitter();

  public transferAccounts$: Observable<
    Array<TradeTransferSources<AccountViewModel>>
  > = of([]);
  public model: TradeTransferModel = new TradeTransferModel(
    new TradeTransferSources<any>('', '', '', undefined),
    0,
  );
  public outOfService = false;
  public loadingForm = true;
  public loadingTransfer = false;
  public allowDropdown = false;
  public destinationLabel = '';
  public isBalanceVisible = false;
  public cashList: { title: string; cash: number }[];

  public isServiceInterrupted = false;

  private storeSubscription: Subscription;
  private isServiceInterruptedSubscription: Subscription;

  constructor(
    private operationService: OperationService,
    private notificationService: NotificationService,
    private store: Store<AppState>,
    private mixpanelService: MixpanelService,
    private contractsService: ContractsService,
  ) {}

  ngOnInit() {
    this.initPositionAndOperationTime();

    const successEvent = new EventSuccess();
    successEvent.setType(this.isUsaContract);
    const successEventData = successEvent.getEventSuccess();

    this.mixpanelService.track(
      TRACKING_EVENTS.screen_transfer.event,
      successEventData,
    );

    this.scanForIsServiceInterrupted();
  }

  ngOnDestroy() {
    [this.storeSubscription, this.isServiceInterruptedSubscription]
      .filter((subscription) => !!subscription)
      .forEach((subscription) => subscription.unsubscribe());
  }

  ngOnChanges(changes: { [propKey: string]: SimpleChange }) {
    const { transferAccounts } = changes;
    if (
      transferAccounts &&
      transferAccounts.currentValue &&
      !isEqual(transferAccounts.currentValue, transferAccounts.previousValue)
    ) {
      const updatedTranferAccounts =
        transferAccounts.currentValue as AccountViewModel[];
      const accountOrigin = updatedTranferAccounts.find(
        (account) =>
          account.legacyContractId === this.currentContract.contractID,
      );
      this.model.origin = new TradeTransferSources(
        accountOrigin.accountId,
        accountOrigin.name,
        this.getLabelPrefix(accountOrigin),
        { ...accountOrigin },
      );

      const filteredTransferAccounts = updatedTranferAccounts
        .filter((account) => account.accountId !== accountOrigin.accountId)
        .sort((a, b) => {
          return a.managementTypeTemplate === b.managementTypeTemplate
            ? 0
            : a.managementTypeTemplate === ACCOUNT_TEMPLATE_TYPES.SMART_CASH
            ? -1
            : 1;
        });

      this.transferAccounts$ = of(
        filteredTransferAccounts.map(
          (account) =>
            new TradeTransferSources(
              account.accountId,
              account.name,
              this.formatStrategyGroupLabel(account),
              { ...account },
            ),
        ),
      );

      this.selectDefaultDestination(filteredTransferAccounts);
      this.allowDropdown = filteredTransferAccounts.length > 1;
    }
  }

  toggleBalanceVisibility() {
    this.isBalanceVisible = !this.isBalanceVisible;
  }

  selectDestination($event: TradeTransferSources<AccountViewModel>) {
    this.model.destination = $event;
    this.destinationLabel = `${this.getLabelPrefix($event.data)} destino`;
  }

  setAmount($amount: number) {
    const parsedAmount = +$amount;
    const validAmount =
      isFinite(parsedAmount) && parsedAmount > -1 ? parsedAmount : 0;
    this.model.amount = validAmount;
  }

  onSubmit() {
    this.loadingTransfer = true;
    const body = {
      amount: this.model.amount,
      target_account_id: this.model.destination.id,
    };

    this.operationService
      .makeTransfer(
        this.model.origin.data.contractId,
        this.model.origin.id,
        body,
      )
      .subscribe(
        (transferResponse) => {
          this.onTranfered.next(true);
          this.loadingTransfer = false;
          const accountPrefix = this.getLabelPrefix(
            this.model.destination.data,
          );
          const transferAmount = new CurrencyPipe('es-MX').transform(
            transferResponse.amount,
            'MXN',
            'symbol-narrow',
            '1.2-3',
          );

          const successEvent = new EventSuccess();
          successEvent.setType(this.isUsaContract);
          const successEventData = successEvent.getEventSuccess();

          this.mixpanelService.track(TRACKING_EVENTS.transfer.event, {
            ...successEventData,
            Target:
              MANAGEMENT_TYPE_EVENT[
                this.model.destination.data.managementTypeTemplate
              ],
          });

          const initialMessage = this.outOfService
            ? 'Transferencia pendiente'
            : 'Transferencia en proceso';
          this.notificationService.showSuccess({
            title: 'Transferencia Exitosa',
            message: `${initialMessage} por el monto de ${transferAmount} a la ${accountPrefix} ${this.model.destination.name}.`,
            eventId: undefined,
            isBusinessError: false,
          });
          const isRealTime = true;
          this.contractsService.getPositionSummaryBatch(
            this.currentContract.contractID,
            isRealTime,
          );
        },
        (error) => {
          this.loadingTransfer = false;

          const eventError = new EventError();
          eventError.setType(this.isUsaContract);
          eventError.setCallback(error.message);
          const eventErrorData = eventError.getEventError();

          this.mixpanelService.track(TRACKING_EVENTS.transfer.event, {
            ...eventErrorData,
            Target:
              MANAGEMENT_TYPE_EVENT[
                this.model.destination.data.managementTypeTemplate
              ],
          });

          this.notificationService.showError({
            title: 'Transferencia no realizada',
            message: `Ocurrió un error al realizar la transferencia, por favor intenta de nuevo.`,
            eventId: undefined,
            isBusinessError: false,
          });
        },
      );
  }

  private initPositionAndOperationTime() {
    const OPEN_HOUR = 7;
    const OPEN_MINUTES = 30;
    const CLOSE_HOUR = 13;
    const CLOSE_MINUTES = 0;

    const cashLayers$ = this.getContractCashLayers$.pipe(
      tap(() => (this.loadingForm = false)),
    );
    const centralHour$ = this.getCentralHourSelector$();

    this.storeSubscription = combineLatest(centralHour$, cashLayers$).subscribe(
      ([centralHour, cashLayers]) => {
        this.model.availableBalance = cashLayers.availableToTransfer;
        this.cashList = cashLayers.cashList;

        const currentHour = new Date(centralHour);

        const openingTime = new Date(centralHour);
        openingTime.setHours(OPEN_HOUR, OPEN_MINUTES, 0);

        const closingTime = new Date(centralHour);
        closingTime.setHours(CLOSE_HOUR, CLOSE_MINUTES, 0);

        const isWhithinWorkingHours =
          moment(currentHour).isSameOrAfter(moment(openingTime)) &&
          moment(currentHour).isSameOrBefore(moment(closingTime));

        this.outOfService = !isWhithinWorkingHours;
      },
    );
  }

  private getCentralHourSelector$() {
    return this.store.select('params').pipe(
      map((params: any) => params.toJS()),
      filter((params) => !!params.centralHour),
      map((params) => params.centralHour),
    );
  }

  private selectDefaultDestination(
    filteredTransferAccounts: AccountViewModel[],
  ) {
    const [accountDestination] = filteredTransferAccounts;
    if (accountDestination) {
      const transferDestination = new TradeTransferSources(
        accountDestination.accountId,
        accountDestination.name,
        this.getLabelPrefix(accountDestination),
        { ...accountDestination },
      );
      this.selectDestination(transferDestination);
    }
  }

  private getLabelPrefix(account: AccountViewModel): string {
    return account &&
      account.managementTypeTemplate === ACCOUNT_TEMPLATE_TYPES.SMART_CASH
      ? 'cuenta'
      : 'estrategia';
  }

  private formatStrategyGroupLabel = (account: AccountViewModel) => {
    const label = capitalize(this.getLabelPrefix(account));
    return account.managementTypeTemplate !== ACCOUNT_TEMPLATE_TYPES.SMART_CASH
      ? `${label}s`
      : label;
  };

  private scanForIsServiceInterrupted() {
    this.isServiceInterruptedSubscription = this.store
      .select('serviceInterruption')
      .pipe(
        map((e: any) => (e && e.toJS()) || undefined),
        filter((state) => state !== undefined),
        pluck('position'),
      )
      .subscribe(
        (isServiceInterrupted: boolean) =>
          (this.isServiceInterrupted = isServiceInterrupted),
      );
  }
}

const MANAGEMENT_TYPE_EVENT: { [key: string]: string } = {
  trading: 'Trading',
  trading_usa: 'Trading USA',
  smart_cash: 'Smart Cash',
  wealth: 'Wealth Management',
};
